import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import useSwipe from "theme/components/helpers/useSwipe";
import { useSliderState } from "./useSliderState";
import SliderComponent from "./SliderComponent";

const Slider = ({
  appearance,
  direction,
  children,
  state,
  showNext,
  showPrev,
  endAnimation,
}) => {
  if (!state || !showNext || !showPrev || !endAnimation) {
    console.warn(
      "Please pass the props from `useSliderState` to the `<Slider>` element."
    );
  }

  if (!Array.isArray(children)) {
    children = [children];
  }

  const currentRef = useRef();
  const contentRef = useRef();
  useEffect(() => {
    if (state.animate && contentRef.current) {
      let element = contentRef.current;
      const onTransitionEnd = () => {
        endAnimation();
        currentRef.current.focus();
      };
      element.addEventListener("transitionend", onTransitionEnd);
      return () => {
        element.removeEventListener("transitionend", onTransitionEnd);
      };
    }
  }, [state.index, state.animate, endAnimation]);

  const [delta, resetDelta] = useSwipe({
    contentRef,
    key: state.index,
    block: state.animate,
    direction: direction,
  });

  const transform =
    direction === "vertical"
      ? state.animate
        ? `translate(0, ${state.direction === "left" ? "100%" : "-100%"})`
        : `translate(0, ${Math.floor(-delta.y)}px)`
      : state.animate
      ? `translate(${state.direction === "left" ? "100%" : "-100%"})`
      : `translate(${Math.floor(-delta.x)}px)`;

  useEffect(() => {
    if (delta.shouldActOnSwipe) {
      resetDelta();
      if ((direction === "vertical" && delta.y > 0) || delta.x > 0) {
        showNext();
      } else {
        showPrev();
      }
    }
  }, [direction, delta, resetDelta, showNext, showPrev]);

  let elementsToDisplay;

  if (Math.abs(state.nextIndex - state.index) > 1) {
    const indexes =
      state.index < state.nextIndex
        ? [state.index - 1, state.index, state.nextIndex]
        : [state.nextIndex, state.index, state.index + 1];
    elementsToDisplay = indexes.map((index) => ({
      element: children[index] || null,
      current: index === state.index,
      previous: index < state.index,
      next: index > state.index,
    }));
  } else {
    elementsToDisplay = [null, ...children, null]
      .slice(state.index, state.index + 3)
      .map((element, index) => ({
        element: element,
        current: index === 1,
        previous: index === 0,
        next: index === 2,
      }));
  }

  return (
    <SliderComponent
      elementsToDisplay={elementsToDisplay}
      appearance={appearance}
      direction={direction}
      state={state}
      delta={delta}
      transform={transform}
      contentRef={contentRef}
      currentRef={currentRef}
    />
  );
};

Slider.propTypes = {
  direction: PropTypes.oneOf(["vertical", "horizontal"]),
  appearance: PropTypes.oneOf(["default", "overflow-visible", "vertical"]),
};

export default Slider;

export { useSliderState };
